import { Popup as PopupType } from '@chessclub/proto-ts/index.popup_service'
import { Box, Button, debounce } from '@mui/material'
import { FC, useCallback, useEffect } from 'react'
import { Dialogs } from '../Contexts/DialogsContext'
import BaseDialog from '../Dialogs/Common/BaseDialog'
import { analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { useScreenSize } from '../hooks/useScreenSize'

export interface PopupProps {
    popup: PopupType
    handleClose: () => void
    handleCTA: (link: string) => void
}

export const Popup: FC<PopupProps> = ({ popup, handleClose, handleCTA }) => {
    if (!popup) return null
    const { isMobile } = useScreenSize()

    const debouncedPopupShown = useCallback(
        debounce(() => {
            analyticsManager.dispatchEvent('popupShown', {
                popupName: popup.name,
                popupId: popup.popupId,
            })
        }, 300),
        [popup],
    )

    useEffect(() => {
        if (Boolean(popup)) {
            debouncedPopupShown()
        }
    }, [])

    const onClose = () => {
        handleClose()
        analyticsManager.dispatchEvent('popupClosed', {
            popupName: popup.name,
            popupId: popup.popupId,
        })
    }

    const handleInternalCTA = (link?: string) => {
        analyticsManager.dispatchEvent('ctaSelected', {
            popupName: popup.name,
            popupId: popup.popupId,
        })
        if (link) {
            handleCTA(link)
        } else {
            handleClose()
        }
    }

    return (
        <BaseDialog
            name={Dialogs.POPUP_SERVICE}
            verticalCentered
            paddingLess
            showClose={popup.closeBtnEnabled}
            onClose={onClose}
            nonClickableBackdrop
            fullScreen={false}
            sx={{
                '& .MuiDialogContent-root': {
                    padding: '0px !important',
                    borderRadius: 0,
                },
                '& .MuiDialog-paper': {
                    width: 'auto !important',
                    maxWidth: 'fit-content !important',
                    minHeight: 'unset',
                    borderRadius: 0,
                },
                '& #close-button': {
                    right: '0px !important',
                    top: '0px !important',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    svg: {
                        filter: 'drop-shadow(1px 3px 3px rgba(0, 0, 0, 0.6))',
                        width: '22px',
                        height: '22px',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: { xs: '344px', md: '600px' },
                    height: { xs: '388px', md: '688px' },
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 0,
                }}
            >
                {/* Content Area */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        width: '100%',
                        backgroundImage: popup.asset ? `url(${popup.asset})` : 'none',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'bottom center',
                        borderRadius: 0,
                    }}
                />

                {/* Action Buttons */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: { xs: '16px', md: '32px' },
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1.5,
                        zIndex: 10,
                    }}
                >
                    {popup.showCtaBtn1 && (
                        <Button
                            variant="contained"
                            size={isMobile ? 'small' : 'large'}
                            sx={{
                                minWidth: { xs: '126px', md: '200px' },
                                backgroundColor: popup.ctaBtnColor1,
                                boxShadow: 'unset',
                                '&:hover': {
                                    backgroundColor: popup.ctaBtnColor1,
                                    filter: 'brightness(0.9)',
                                    boxShadow: 'unset',
                                },
                                '&:focus, &:focus-visible, &:focus-within, &:active, &:disabled': {
                                    boxShadow: 'unset',
                                },
                            }}
                            onClick={() => handleInternalCTA(popup.ctaBtnLink1)}
                        >
                            {popup.ctaBtnText1}
                        </Button>
                    )}

                    {popup.showCtaBtn2 && (
                        <Button
                            variant="contained"
                            size={isMobile ? 'small' : 'large'}
                            sx={{
                                minWidth: { xs: '126px', md: '200px' },
                                backgroundColor: popup.ctaBtnColor2,
                                boxShadow: 'unset',
                                '&:hover': {
                                    backgroundColor: popup.ctaBtnColor2,
                                    filter: 'brightness(0.9)',
                                    boxShadow: 'unset',
                                },
                                '&:focus, &:focus-visible, &:focus-within, &:active, &:disabled': {
                                    boxShadow: 'unset',
                                },
                            }}
                            onClick={() => handleInternalCTA(popup.ctaBtnLink2)}
                        >
                            {popup.ctaBtnText2}
                        </Button>
                    )}
                </Box>
            </Box>
        </BaseDialog>
    )
}
